import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// SPEC-web-planner-023 AC-2. One person-name rule shared by the contact drawer's edit form
// and the quick-add Full name. The shared nameValidator (ASCII letters only) is unchanged.

/** Letters, combining marks, whitespace, apostrophe, full stop and hyphen. */
const NAME_CHARS = /^[\p{L}\p{M}\s'.-]+$/u;
const HAS_LETTER = /\p{L}/u;

/** Maximum length of firstName and of lastName (ContactDto.cs:53-54). */
export const NAME_PART_MAX = 50;

/**
 * Trimmed value must be made only of name characters and hold at least one letter.
 * An empty value passes: pair with Validators.required.
 */
export function personNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = String(control.value ?? '').trim();
        if (value === '') {
            // Whitespace-only input (non-empty raw value) is not a name.
            return String(control.value ?? '') === '' ? null : { personName: true };
        }
        return NAME_CHARS.test(value) && HAS_LETTER.test(value) ? null : { personName: true };
    };
}

/** Each of the first-name and last-name parts is at most 50 characters. */
export function namePartLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const { firstName, lastName } = splitFullName(String(control.value ?? ''));
        return firstName.length > NAME_PART_MAX || lastName.length > NAME_PART_MAX
            ? { namePart: true }
            : null;
    };
}

/**
 * Split "Full name" as quick-add does: first token is firstName, the rest is
 * lastName, or '-' when there is none.
 */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
    const trimmed = (fullName || '').trim();
    const parts = trimmed.split(/\s+/);
    const firstName = parts.shift() || trimmed;
    const lastName = parts.length ? parts.join(' ') : '-';
    return { firstName, lastName };
}

/**
 * SPEC-web-planner-036 AC-3: one "name or email" rule for the Users list and the Teams member lists.
 * An invitee who has not yet set a name is stored with DisplayName = email, so the email is the name line.
 * Invitees with names keep them.
 */
export function nameOrEmail(
    u: { firstName?: string | null; lastName?: string | null; displayName?: string | null; email?: { value?: string | null } | string | null } | null | undefined,
    fallback = 'Unknown user',
): string {
    const parts = `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim();
    const email = typeof u?.email === 'string' ? u.email : u?.email?.value;
    return parts || (u?.displayName ?? '').trim() || (email ?? '').trim() || fallback;
}
